import React, { useCallback, useEffect, useState } from 'react';
import { Dropdown, message, Modal, Spin } from 'antd';
import type { MenuProps } from 'antd';
import { BarChartOutlined, MoreOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { usePartnershipContext } from '../../../core/context/PartnershipContext';
import { m1Service, M1DifferenceRow, M1Summary } from './M1Service';
import { MODAL_STYLES, MODAL_TITLE_STYLE } from '../../../styles/modalStyles';
import styles from './M1Differences.module.css';

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtCurrency(n: number): string {
  if (n == null) return '—';
  return '$' + Math.abs(n).toLocaleString('en-US');
}

function fmtDiff(n: number): string {
  if (n == null) return '—';
  const abs = '$' + Math.abs(n).toLocaleString('en-US');
  if (n > 0) return '+' + abs;
  if (n < 0) return '−' + abs;
  return abs;
}

// ── Add/Edit Modal ─────────────────────────────────────────────────────────────

interface ModalFormData {
  categoryName: string;
  categoryType: string;
  bookAmount: string;
  taxAmount: string;
}

const CATEGORY_TYPES = ['Depreciation', 'Accrual', 'Prepaid', 'Reserve', 'Inventory', 'Other'];

const AddEditModal: React.FC<{
  mode: 'create' | 'edit';
  initial?: M1DifferenceRow;
  partnershipId: string;
  taxYear: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ mode, initial, partnershipId, taxYear, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<ModalFormData>({
    categoryName: initial?.categoryName ?? '',
    categoryType: initial?.categoryType ?? 'Depreciation',
    bookAmount:   initial != null ? String(initial.bookAmount) : '',
    taxAmount:    initial != null ? String(initial.taxAmount)  : '',
  });

  const set = (k: keyof ModalFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(prev => ({ ...prev, [k]: e.target.value }));

  const parseNum = (s: string) => {
    const n = parseFloat(s.replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  const previewDiff = parseNum(form.bookAmount) - parseNum(form.taxAmount);

  const handleSave = async () => {
    if (!form.categoryName.trim()) { message.error('Category name is required'); return; }
    setSaving(true);
    try {
      if (mode === 'create') {
        await m1Service.createCategory({
          partnershipId,
          taxYear,
          categoryName: form.categoryName.trim(),
          categoryType: form.categoryType,
          bookAmount:   parseNum(form.bookAmount),
          taxAmount:    parseNum(form.taxAmount),
        });
      } else if (initial) {
        await m1Service.updateCategory(initial.id, {
          categoryName: form.categoryName.trim(),
          categoryType: form.categoryType,
          bookAmount:   parseNum(form.bookAmount),
          taxAmount:    parseNum(form.taxAmount),
        });
      }
      message.success(`M-1 balance ${mode === 'create' ? 'created' : 'updated'}`);
      onSaved();
    } catch {
      message.error(`Failed to ${mode === 'create' ? 'create' : 'update'} M-1 balance`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={<span style={MODAL_TITLE_STYLE}>{mode === 'create' ? 'Add M-1 Balance' : 'Edit M-1 Balance'}</span>}
      centered
      width={480}
      styles={MODAL_STYLES}
    >
      <div className={styles.formStack} style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 4 }}>
        <div className={styles.formRow}>
          <label className={styles.formLabel}>Category Name <span className={styles.req}>*</span></label>
          <input className={styles.formInput} value={form.categoryName} onChange={set('categoryName')} placeholder="e.g. Depreciation difference" />
        </div>
        <div className={styles.formRow}>
          <label className={styles.formLabel}>Category Type</label>
          <select className={styles.formSelect} value={form.categoryType} onChange={set('categoryType')}>
            {CATEGORY_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className={styles.formRow}>
            <label className={styles.formLabel}>Book Amount</label>
            <input className={styles.formInput} value={form.bookAmount} onChange={set('bookAmount')} placeholder="0" style={{ textAlign: 'right', fontFamily: 'monospace' }} />
          </div>
          <div className={styles.formRow}>
            <label className={styles.formLabel}>Tax Amount</label>
            <input className={styles.formInput} value={form.taxAmount} onChange={set('taxAmount')} placeholder="0" style={{ textAlign: 'right', fontFamily: 'monospace' }} />
          </div>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--ce-border)', borderRadius: 6, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--ce-text-muted)' }}>Auto-computed difference</span>
          <span style={{ fontFamily: 'monospace', fontWeight: 700, color: previewDiff > 0 ? '#22c55e' : previewDiff < 0 ? '#ef4444' : 'var(--ce-text-muted)' }}>
            {fmtDiff(previewDiff)}
          </span>
        </div>
        <div className={styles.formActions}>
          <button className={styles.btnOutline} onClick={onClose} disabled={saving}>Cancel</button>
          <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : mode === 'create' ? 'Save M-1 Balance' : 'Update'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ── M1Differences ──────────────────────────────────────────────────────────────

export const M1Differences: React.FC = () => {
  const { selectedPartnership, taxYear } = usePartnershipContext();

  const [summary, setSummary]       = useState<M1Summary | null>(null);
  const [loading, setLoading]       = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter]              = useState<'All' | 'Active'>('All');

  const [showAddModal, setShowAddModal]   = useState(false);
  const [editTarget, setEditTarget]       = useState<M1DifferenceRow | null>(null);

  const fetchData = useCallback(async (partnershipId: string, year: string) => {
    setLoading(true);
    try {
      const data = await m1Service.getM1Differences(partnershipId, year);
      setSummary(data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === undefined) {
        setSummary({ totalCumulativeBookAmount: 0, totalCumulativeTaxAmount: 0, netCumulativeDifference: 0, categories: [] });
      } else {
        message.error('Failed to load M-1 differences');
        setSummary(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPartnership) {
      fetchData(selectedPartnership.id, taxYear);
    } else {
      setSummary(null);
    }
  }, [selectedPartnership, taxYear, fetchData]);

  const rows = summary?.categories ?? [];
  const search = searchText.toLowerCase();
  const filtered = rows.filter(r =>
    r.categoryName.toLowerCase().includes(search) ||
    r.categoryType.toLowerCase().includes(search),
  );

  const rowMenu = (record: M1DifferenceRow): MenuProps['items'] => [
    {
      key: 'edit',
      label: 'Edit',
      onClick: ({ domEvent }) => { domEvent.stopPropagation(); setEditTarget(record); },
    },
    {
      key: 'delete',
      label: 'Delete',
      danger: true,
      onClick: ({ domEvent }) => {
        domEvent.stopPropagation();
        Modal.confirm({
          title: 'Delete M-1 balance?',
          content: `This will permanently remove "${record.categoryName}".`,
          okText: 'Delete',
          okButtonProps: { danger: true },
          onOk: async () => {
            message.info('Delete not yet available — backend pending');
          },
        });
      },
    },
  ];

  const net = summary?.netCumulativeDifference ?? 0;

  return (
    <div className={styles.pageWrap}>
      <div className={styles.pageHead}>
        <div>
          <h1 className={styles.pageTitle}>M-1 Differences</h1>
          {selectedPartnership && (
            <p className={styles.pageSub}>{selectedPartnership.name} · Book vs. tax differences by category</p>
          )}
        </div>
        <div className={styles.headActions}>
          <button className={styles.btnOutline} onClick={() => message.info('Import coming soon')}>
            Import
          </button>
          <button className={styles.btnOutline} onClick={() => message.info('Edit coming soon')}>
            Edit
          </button>
          <button className={styles.btnPrimary} onClick={() => setShowAddModal(true)}>
            <PlusOutlined /> Add M-1 Balance
          </button>
        </div>
      </div>

      {!selectedPartnership ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}><BarChartOutlined style={{ fontSize: 32, color: '#ADADAD' }} /></div>
          <div className={styles.emptyText}>Select a client and partnership to view M-1 differences</div>
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div className={styles.kpiRow}>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Total Cumulative Book Amount</div>
              <div className={styles.kpiValue}>{fmtCurrency(summary?.totalCumulativeBookAmount ?? 0)}</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Total Cumulative Tax Amount</div>
              <div className={styles.kpiValue}>{fmtCurrency(summary?.totalCumulativeTaxAmount ?? 0)}</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Net Cumulative Difference</div>
              <div className={net > 0 ? styles.kpiValuePos : net < 0 ? styles.kpiValueNeg : styles.kpiValue}>
                {fmtDiff(net)}
              </div>
            </div>
          </div>

          {/* Filter row */}
          <div className={styles.filterRow}>
            <div className={styles.searchWrap}>
              <SearchOutlined className={styles.searchIcon} />
              <input
                className={styles.searchInput}
                placeholder="Search by category name..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </div>
            <select className={styles.filterSelect} defaultValue={statusFilter}>
              <option>All</option>
              <option>Active</option>
            </select>
          </div>

          {/* Table */}
          {loading ? (
            <div className={styles.loadingState}><Spin /></div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>M-1 Category</th>
                    <th style={{ width: 140, textAlign: 'right' }}>Book Amount</th>
                    <th style={{ width: 140, textAlign: 'right' }}>Tax Amount</th>
                    <th style={{ width: 160, textAlign: 'right' }}>Cumulative Difference</th>
                    <th style={{ width: 48 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={5} className={styles.emptyCell}>
                        {searchText
                          ? 'No categories match your search'
                          : 'No M-1 differences recorded for this partnership. Add categories manually or use Import.'}
                      </td>
                    </tr>
                  ) : (
                    filtered.map(record => (
                      <tr key={record.id} className={styles.tableRow}>
                        <td>
                          <div className={styles.cellName}>{record.categoryName}</div>
                          <div className={styles.cellSub}>{record.categoryType}</div>
                        </td>
                        <td className={styles.cellMono} style={{ textAlign: 'right' }}>
                          {record.bookAmount.toLocaleString('en-US')}
                        </td>
                        <td className={styles.cellMono} style={{ textAlign: 'right' }}>
                          {record.taxAmount.toLocaleString('en-US')}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <span className={
                            record.cumulativeDifference > 0 ? styles.diffPos :
                            record.cumulativeDifference < 0 ? styles.diffNeg :
                            styles.diffNeutral
                          }>
                            {fmtDiff(record.cumulativeDifference)}
                          </span>
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <Dropdown menu={{ items: rowMenu(record) }} trigger={['click']}>
                            <button className={styles.actionBtn} aria-label="Actions">
                              <MoreOutlined />
                            </button>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {filtered.length > 0 && (
                <div className={styles.tableFooter}>
                  Showing {filtered.length} of {rows.length} categor{rows.length !== 1 ? 'ies' : 'y'}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {showAddModal && selectedPartnership && (
        <AddEditModal
          mode="create"
          partnershipId={selectedPartnership.id}
          taxYear={taxYear}
          onClose={() => setShowAddModal(false)}
          onSaved={() => { setShowAddModal(false); fetchData(selectedPartnership.id, taxYear); }}
        />
      )}

      {editTarget && selectedPartnership && (
        <AddEditModal
          mode="edit"
          initial={editTarget}
          partnershipId={selectedPartnership.id}
          taxYear={taxYear}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); fetchData(selectedPartnership.id, taxYear); }}
        />
      )}
    </div>
  );
};
